import { useState, useCallback, useMemo } from 'react';

import {
  useGetSingleStockValuation,
  SingleStockValuationFilter,
  StockValuationDetailItem,
} from '../../api/accounts';
import { useAppContext } from '../../contexts/Storage';

interface UseSingleStockValuationOptions {
  entityId?: number;
  entityName?: string;
  recordId?: number;
}

// Helper to get default date range (1 year back to today)
const getDefaultDateRange = () => {
  const today = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(today.getFullYear() - 1);

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    start_date: `${formatDate(oneYearAgo)} 00:00:00`,
    end_date: `${formatDate(today)} 23:59:59`,
  };
};

const useSingleStockValuation = (options: UseSingleStockValuationOptions = {}) => {
  const { entityId, entityName, recordId: initialRecordId } = options;
  const { user, selectedRecord } = useAppContext();

  // Use provided recordId or fall back to selected record
  const recordId = initialRecordId ?? selectedRecord?.id;
  const userId = user?.id;

  // Get default date range
  const defaultDates = getDefaultDateRange();

  // Filter state - include default dates as API requires them
  const [filters, setFilters] = useState<Partial<SingleStockValuationFilter>>({
    per_page: 15,
    page: 1,
    start_date: defaultDates.start_date,
    end_date: defaultDates.end_date,
  });

  // API hook - only fetch when we have required params
  const hasRequiredParams = !!entityId && !!recordId && !!userId;
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useGetSingleStockValuation({
    filters: {
      ...filters,
      entity_id: entityId,
      record_id: recordId,
      user_id: userId,
    },
    enabled: hasRequiredParams,
  });

  // Derived data
  const items = useMemo(() => data?.items ?? [], [data?.items]);
  const pagination = useMemo(() => data?.pagination, [data?.pagination]);

  // Filter handlers
  const updateFilter = useCallback((updates: Partial<SingleStockValuationFilter>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleDateFilter = useCallback((startDate?: string, endDate?: string) => {
    const defaults = getDefaultDateRange();
    updateFilter({
      start_date: startDate ? `${startDate} 00:00:00` : defaults.start_date,
      end_date: endDate ? `${endDate} 23:59:59` : defaults.end_date,
      page: 1,
    });
  }, [updateFilter]);

  const handlePageChange = useCallback((page: number) => {
    updateFilter({ page });
  }, [updateFilter]);

  const handlePerPageChange = useCallback((perPage: number) => {
    updateFilter({ per_page: perPage, page: 1 });
  }, [updateFilter]);

  const resetFilters = useCallback(() => {
    const defaultDates = getDefaultDateRange();
    setFilters({
      per_page: 15,
      page: 1,
      start_date: defaultDates.start_date,
      end_date: defaultDates.end_date,
    });
  }, []);

  // Map items to TripleColumnPage format
  const tripleColumnItems = useMemo(() => {
    return items.map((item: StockValuationDetailItem) => {
      const isReceipt = item.isReceipt;

      return {
        id: item.id,
        date: item.date,
        // Issue column (credit actions - stock going out, e.g., Sales)
        issueQuantity: !isReceipt ? item.quantity : undefined,
        issuePrice: !isReceipt ? item.rate : undefined,
        issueAmount: !isReceipt ? item.amount : undefined,
        // Receipt column (debit actions - stock coming in, e.g., Purchases)
        receiptQuantity: isReceipt ? item.quantity : undefined,
        receiptPrice: isReceipt ? item.rate : undefined,
        receiptAmount: isReceipt ? item.amount : undefined,
        // Balance column
        balanceQuantity: item.balanceQuantity,
        balancePrice: item.balanceRate,
        balanceAmount: item.balanceAmount,
      };
    });
  }, [items]);

  return {
    // Data
    items,
    tripleColumnItems,
    pagination,
    entityName,

    // Loading states
    isLoading,
    hasRequiredParams,

    // Error state
    error,

    // Filter state and handlers
    filters,
    handleDateFilter,
    handlePageChange,
    handlePerPageChange,
    resetFilters,
    updateFilter,

    // Refetch
    refetch,
  };
};

export default useSingleStockValuation;
